import type { ModuleId } from '@tahi/core';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { navPinOf, pinnedModules } from '../../data/access';
import { resetNavPins, setNavPins } from '../../data/commands/modules';
import { useCommandEnv, useModuleStates, useUsable } from '../../data/hooks';
import { getWebModule, listWebModules } from '../../modules/registry';
import { Button } from '../../ui/Button';

export function NavPinsEditor() {
  const env = useCommandEnv();
  const usable = useUsable();
  const { data: states = [] } = useModuleStates();
  const registered = listWebModules().map((m) => m.id);
  const pinned = pinnedModules(usable, states, registered);
  const others = registered.filter((id) => id !== 'today' && usable(id) && !pinned.includes(id));
  const custom = states.some((s) => navPinOf(s) !== undefined);
  const label = (id: ModuleId) => getWebModule(id)?.navLabel ?? id;
  const save = (ids: ModuleId[]) => void setNavPins(env, ids);
  const move = (i: number, dir: -1 | 1) => {
    const next = [...pinned];
    const j = i + dir;
    [next[i], next[j]] = [next[j] as ModuleId, next[i] as ModuleId];
    save(next);
  };

  if (pinned.length === 0 && others.length === 0) {
    return <p className="text-sm text-muted">Turn on a module to pin it to the bottom bar.</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted">
        Pick up to three modules for the bottom bar. Today and Modules are always there.
      </p>
      <ol aria-label="Pinned modules" className="flex flex-col gap-2">
        {pinned.map((id, i) => (
          <li key={id} className="flex items-center gap-1 rounded-2xl bg-surface p-1 pl-4">
            <span className="flex-1">{label(id)}</span>
            <Button
              variant="ghost"
              className="px-3"
              aria-label={`Move ${label(id)} up`}
              disabled={i === 0}
              onClick={() => move(i, -1)}
            >
              <ChevronUp aria-hidden className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              className="px-3"
              aria-label={`Move ${label(id)} down`}
              disabled={i === pinned.length - 1}
              onClick={() => move(i, 1)}
            >
              <ChevronDown aria-hidden className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              className="px-3"
              aria-label={`Unpin ${label(id)}`}
              onClick={() => save(pinned.filter((x) => x !== id))}
            >
              <X aria-hidden className="h-5 w-5" />
            </Button>
          </li>
        ))}
      </ol>
      {others.length > 0 ? (
        <ul aria-label="Other modules" className="flex flex-col gap-2">
          {others.map((id) => (
            <li
              key={id}
              className="flex items-center justify-between gap-2 rounded-2xl bg-surface p-1 pl-4"
            >
              <span>{label(id)}</span>
              <Button
                aria-label={`Pin ${label(id)}`}
                disabled={pinned.length >= 3}
                onClick={() => save([...pinned, id])}
              >
                Pin
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {pinned.length >= 3 && others.length > 0 ? (
        <p className="text-xs text-muted">Unpin one to add another.</p>
      ) : null}
      {custom ? (
        <Button variant="ghost" onClick={() => void resetNavPins(env)}>
          Use the default
        </Button>
      ) : null}
    </div>
  );
}
